import { inject, Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { Observable } from "rxjs";
import { EntityApiService } from "../../../../shared/store/entity-store/entity-api.service";
import { environment } from "../../../../../environments/environment";
import { map } from "rxjs/operators";
import { PiedDeChaineBuilder } from "../../../pied-de-chaine/models/pied-de-chaine-builder.model";
import { ProcessEtapeOpe } from "../../models/peo.model";
import { QueryParam } from "../../../../shared/store/store-types.model";
import { queryParamToHttp } from "../../../../shared/store/utils/store-utils.service";
import { Checklist } from "../../../list/models/checklist.model";
import { safeFirstValueFrom } from "../../../../shared/utils/safe-first-value-from";

@Injectable({ providedIn: "root" })
export class PeoApiService implements EntityApiService<ProcessEtapeOpe> {
  httpClient = inject(HttpClient);

  loadOne(id: string): Observable<ProcessEtapeOpe> {
    return this.httpClient.get<ProcessEtapeOpe>(
      `${environment.api()}/peo/id/${id}`,
    );
  }

  loadAll(query?: QueryParam): Observable<ProcessEtapeOpe[]> {
    let params = queryParamToHttp(query);
    return this.httpClient.get<ProcessEtapeOpe[]>(
      `${environment.api()}/peo/search`,
      { params },
    );
  }

  loadAllEagerForChecklist(checklistId: string): Observable<ProcessEtapeOpe[]> {
    return this.httpClient.get<ProcessEtapeOpe[]>(
      `${environment.api()}/peo/eager/checklistId/${checklistId}`,
    );
  }

  create(entity: ProcessEtapeOpe): Observable<ProcessEtapeOpe> {
    return this.httpClient.post<ProcessEtapeOpe>(
      `${environment.api()}/peo`,
      entity,
    );
  }

  remove(id: string): Observable<string> {
    return this.httpClient
      .delete<PiedDeChaineBuilder>(`${environment.api()}/peo/id/${id}`)
      .pipe(map((o) => id));
  }

  update(entity: ProcessEtapeOpe): Observable<ProcessEtapeOpe> {
    return this.httpClient.put<ProcessEtapeOpe>(
      `${environment.api()}/peo/id/${entity.id}`,
      entity,
    );
  }

  async startPeo(id: string) {
    return await safeFirstValueFrom(
      this.httpClient.post<ProcessEtapeOpe>(
        `${environment.api()}/peo/start/${id}`,
        null,
      ),
    );
  }

  async endPeo(id: string) {
    return await safeFirstValueFrom(
      this.httpClient.post<ProcessEtapeOpe>(
        `${environment.api()}/peo/end/${id}`,
        null,
      ),
    );
  }

  async endPeoForChecklist(id: string, checklistId: string) {
    return await safeFirstValueFrom(
      this.httpClient.post<{ peo: ProcessEtapeOpe; checklist: Checklist }>(
        `${environment.api()}/checklist/end_peo/${id}/${checklistId}`,
        null,
      ),
    );
  }

  /** Étape de checklist d'OF déclarée non applicable (`true`) ou rendue applicable (`false`). */
  async setNonApplicableForChecklist(id: string, checklistId: string, nonApplicable: boolean) {
    return await safeFirstValueFrom(
      this.httpClient.post<{ peo: ProcessEtapeOpe; checklist: Checklist }>(
        `${environment.api()}/checklist/peo_non_applicable/${id}/${checklistId}`,
        { nonApplicable },
      ),
    );
  }

  /**
   * Étape de procédé d'un POSTE déclarée NON APPLICABLE, ou rendue applicable (06/10/2026,
   * `POST /peo/:id/non-applicable`), PEO de poste seulement : une étape de checklist garde
   * `setNonApplicableForChecklist`. Refus : 409 `PEO.NA_POSTE_PAS_EN_COURS` / `PEO.NA_ETAPE_VALIDEE`.
   */
  async setNonApplicable(id: string, nonApplicable: boolean, commentaire?: string | null) {
    return await safeFirstValueFrom(
      this.httpClient.post<ProcessEtapeOpe>(
        `${environment.api()}/peo/${id}/non-applicable`,
        { nonApplicable, commentaire: commentaire ?? null },
      ),
    );
  }

  async resetEndPeo(id: string) {
    return await safeFirstValueFrom(
      this.httpClient.post<ProcessEtapeOpe>(
        `${environment.api()}/peo/resetEndPeo/${id}`,
        null,
      ),
    );
  }
}
